import React from 'react';
import './styles.css';
import lob from '../../assets/lobstr.webp';
import red from '../../assets/red.webp';
import tiager from '../../assets/tiager.webp';
import seabass from '../../assets/seabass.webp';
import blue from '../../assets/blue.webp';
import whitePomfret from '../../assets/White-pomfret.webp';
import seerFish from '../../assets/Seer-fish.webp';
import yellowFintuna from '../../assets/Yellow-fintuna.webp';
import salmon from '../../assets/Salmon.webp';
import seaWhitePrawns from '../../assets/Sea-white-prawns.webp';
import blackPomfret from '../../assets/Black-pomfret.webp';

type CatalogProps = {
  limit?: number;
  onViewFull?: () => void;
};

export const catalogItems = [
  {
    name: 'White pomfret',
    note: 'Whole | 500-900g',
    image: whitePomfret,
    description: 'Mild, buttery pomfret cleaned and ready for pan-fry, steaming, or curries.',
  },
  {
    name: 'Seer fish',
    note: 'Steaks | 1-2 kg catch',
    image: seerFish,
    description: 'Thick-cut seer fish (king mackerel) steaks for grills, fries, and gravies.',
  },
  {
    name: 'Yellow fin tuna',
    note: 'Loins | Sashimi-friendly',
    image: yellowFintuna,
    description: 'Firm yellow fin tuna loins ideal for searing, poke, tartare, or steaks.',
  },
  {
    name: 'Salmon',
    note: 'Chilled | Trimmed fillet',
    image: salmon,
    description: 'Boneless salmon fillet with balanced fat for grills, sushi prep, or smoking.',
  },
  {
    name: 'Sea white prawns',
    note: '31/40 | Head-on',
    image: seaWhitePrawns,
    description: 'Sweet sea white prawns with a clean snap—great for fry-ups and curries.',
  },
  {
    name: 'Black pomfret',
    note: 'Whole | 600-900g',
    image: blackPomfret,
    description: 'Firm black pomfret suited to fry, grill, or gassi-style coastal gravies.',
  },
  {
    name: 'Lobster',
    note: 'Wild | 1.2-1.8 kg',
    image: lob,
    description: 'Live wild-caught lobster ideal for premium grills and signature platters.',
  },
  {
    name: 'Red snapper',
    note: 'Line caught | 800g+',
    image: red,
    description: 'Whole red snapper with firm, flaky meat perfect for grill, fry, or tandoor.',
  },
  {
    name: 'Tiger prawns',
    note: 'U/10 | Premium',
    image: tiager,
    description: 'Large tiger prawns with a firm bite, ideal for grills, curries, and starters.',
  },
  {
    name: 'Seabass',
    note: 'Whole | 600-900g',
    image: seabass,
    description: 'Clean, delicate seabass ideal for fillets, steaming, or pan-searing.',
  },
  {
    name: 'Blue crab',
    note: 'Live | Grade A',
    image: blue,
    description: 'Live blue crab with sweet meat, perfect for coastal curries and specials.',
  },
];

function Catalog({ limit, onViewFull }: CatalogProps) {
  const items = typeof limit === 'number' ? catalogItems.slice(0, limit) : catalogItems;
  const canViewFull = typeof limit === 'number' && limit < catalogItems.length && !!onViewFull;
  const productSchema = {
    '@context': 'https://schema.org',
    '@graph': catalogItems.map((item) => ({
      '@type': 'Product',
      name: item.name,
      description: item.description,
      image: `https://frfisheries.com${item.image.startsWith('/') ? '' : '/'}${item.image}`,
      category: 'Seafood',
      brand: {
        '@type': 'Organization',
        name: 'FR Fisheries',
      },
    })),
  };

  return (
    <section className="catalog" id="catalog">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(productSchema) }}
      />
      <div className="catalog-head scroll-reveal">
        <h2>Live catalog</h2>
        <p>Curated by coastal seasons. Updated every 15 minutes with exact landed volumes.</p>
      </div>
      <div className="catalog-grid">
        {items.map((item) => (
          <article key={item.name} className="catalog-card reveal">
            <img
              className="catalog-img"
              src={item.image}
              alt={`Fresh ${item.name} seafood`}
              loading="lazy"
              decoding="async"
            />
            <div className="catalog-top">
              <h3>{item.name}</h3>
              <span className="pill">Today</span>
            </div>
            <p>{item.note}</p>
          </article>
        ))}
      </div>
      {canViewFull && (
        <div className="catalog-actions">
          <button type="button" className="btn btn-outline" onClick={onViewFull}>
            View full catalog
          </button>
        </div>
      )}
    </section>
  );
}

export default Catalog;
